// File: ReportSales.jsx
import React, { useMemo, useState, useEffect } from "react";
import { Grid } from "@aws-amplify/ui-react";
import moment from "moment";
import {
  CCard,
  CCardBody,
  CCardHeader,
  CRow,
  CCol,
  CTable,
  CTableHead,
  CTableBody,
  CTableRow,
  CTableHeaderCell,
  CTableDataCell,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
} from "@coreui/react";
import { Bar, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  ArcElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Title,
} from "chart.js";
import ConfirmModal from "components/utils/ConfirmModal";

ChartJS.register(BarElement, ArcElement, CategoryScale, LinearScale, Tooltip, Legend, Title);

const sampleData = {
  farms: [
    { id: "farm-1", name: "Finca La Esperanza", active: true },
    { id: "farm-2", name: "Finca El Roble", active: true },
  ],
  lots: [
    { id: "lot-1", farmId: "farm-1", name: "Lote 1", cropUnit: "Café", active: true },
    { id: "lot-2", farmId: "farm-1", name: "Lote 2", cropUnit: "Banano", active: true },
    { id: "lot-3", farmId: "farm-2", name: "Lote A", cropUnit: "Cacao", active: true },
  ],
  collaborators: [
    { id: "col-1", name: "Juan Pérez" },
    { id: "col-2", name: "María Gómez" },
    { id: "col-3", name: "Carlos Ruiz" },
  ],
  saleClassifications: [
    { id: "sc-1", name: "Local" },
    { id: "sc-2", name: "Exportación" },
    { id: "sc-3", name: "Consumo" },
  ],
  // saleRecords: fictitious sales
  saleRecords: [
    { id: "s1", date: "2025-10-01", farmId: "farm-1", farmName: "Finca La Esperanza", lotId: "lot-1", lotName: "Lote 1", cropUnit: "Café", performedById: "col-1", performedByName: "Juan Pérez", saleClassification: "Local", totalSale: 1200000, buyerName: "Comprador A" },
    { id: "s2", date: "2025-10-03", farmId: "farm-1", farmName: "Finca La Esperanza", lotId: "lot-2", lotName: "Lote 2", cropUnit: "Banano", performedById: "col-2", performedByName: "María Gómez", saleClassification: "Exportación", totalSale: 2400000, buyerName: "Exportadora B" },
    { id: "s3", date: "2025-10-05", farmId: "farm-2", farmName: "Finca El Roble", lotId: "lot-3", lotName: "Lote A", cropUnit: "Cacao", performedById: "col-3", performedByName: "Carlos Ruiz", saleClassification: "Consumo", totalSale: 600000, buyerName: "Mercado C" },
    { id: "s4", date: "2025-09-20", farmId: "farm-1", farmName: "Finca La Esperanza", lotId: "lot-1", lotName: "Lote 1", cropUnit: "Café", performedById: "col-1", performedByName: "Juan Pérez", saleClassification: "Local", totalSale: 900000, buyerName: "Comprador D" },
    { id: "s5", date: "2025-09-28", farmId: "farm-2", farmName: "Finca El Roble", lotId: "lot-3", lotName: "Lote A", cropUnit: "Cacao", performedById: "col-2", performedByName: "María Gómez", saleClassification: "Exportación", totalSale: 1800000, buyerName: "Exportadora E" },
  ],
};

export default function ReportSales(props) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const data = props.data || sampleData || { farms: [], lots: [], saleRecords: [], collaborators: [] };
  // filters: period (start/end), farm, lot
  const saleDates = useMemo(() => (data.saleRecords || []).map((s) => s.date).sort(), [data]);
  const defaultStart = saleDates[0] || moment().format("YYYY-MM-DD");
  const defaultEnd = saleDates[saleDates.length - 1] || moment().format("YYYY-MM-DD");

  const farms = useMemo(() => (data.farms || []).filter((f) => f.active), [data]);
  const [selectedFarmId, setSelectedFarmId] = useState(farms[0]?.id || "");
  const lotsByFarm = useMemo(() => (data.lots || []).filter((l) => l.farmId === selectedFarmId && l.active), [data, selectedFarmId]);
  const [selectedLotId, setSelectedLotId] = useState(lotsByFarm[0]?.id || "");

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);

  useEffect(() => { setSelectedFarmId(farms[0]?.id || ""); }, [farms]);
  useEffect(() => { setSelectedLotId(lotsByFarm[0]?.id || ""); }, [lotsByFarm]);
  useEffect(() => { setStartDate(defaultStart); setEndDate(defaultEnd); }, [defaultStart, defaultEnd]);

  // filtered sale records according to period, farm, lot
  const filteredSales = useMemo(() => {
    const records = data.saleRecords || [];
    const s = moment(startDate, "YYYY-MM-DD");
    const e = moment(endDate, "YYYY-MM-DD").endOf("day");
    return records.filter((r) => {
      const d = moment(r.date, "YYYY-MM-DD");
      if (!d.isBetween(s, e, null, "[]")) return false;
      if (selectedFarmId && r.farmId !== selectedFarmId) return false;
      if (selectedLotId && r.lotId !== selectedLotId) return false;
      return true;
    });
  }, [data, startDate, endDate, selectedFarmId, selectedLotId]);

  // totals
  const totalSalesAmount = useMemo(() => filteredSales.reduce((acc, r) => acc + (Number(r.totalSale) || 0), 0), [filteredSales]);
  const totalRecords = filteredSales.length;

  // charts:
  // Bar: total sales by classification (aggregation of filteredSales)
  const salesByClassification = useMemo(() => {
    return filteredSales.reduce((acc, r) => {
      const cls = r.saleClassification || "Otro";
      acc[cls] = (acc[cls] || 0) + Number(r.totalSale || 0);
      return acc;
    }, {});
  }, [filteredSales]);

  // Pie: distribution of sales by farm (filteredSales)
  const salesByFarm = useMemo(() => {
    return filteredSales.reduce((acc, r) => {
      const f = r.farmName || r.farmId || "Sin finca";
      acc[f] = (acc[f] || 0) + Number(r.totalSale || 0);
      return acc;
    }, {});
  }, [filteredSales]);

  const classificationLabels = useMemo(() => Object.keys(salesByClassification), [salesByClassification]);
  const classificationValues = useMemo(() => classificationLabels.map((k) => salesByClassification[k]), [classificationLabels, salesByClassification]);

  const farmLabels = useMemo(() => Object.keys(salesByFarm), [salesByFarm]);
  const farmValues = useMemo(() => farmLabels.map((k) => salesByFarm[k]), [farmLabels, salesByFarm]);

  // pastel colors
  const PASTEL_COLORS = [
    "#90d47dff",
    "#f09bb4ff",
    "#fcdc86ff",
    "#8eb4ffff",
    "#ce99f7ff",
    "#ffbe90ff",
    "#98eccdff",
  ];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const colorsForClass = useMemo(() => classificationLabels.map((_, i) => PASTEL_COLORS[i % PASTEL_COLORS.length]), [classificationLabels]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const colorsForFarm = useMemo(() => farmLabels.map((_, i) => PASTEL_COLORS[i % PASTEL_COLORS.length]), [farmLabels]);

  const chartHeightPx = props.chartHeightPx || 360;

  const barData = useMemo(() => ({
    labels: classificationLabels,
    datasets: [{
      label: "Total venta",
      data: classificationValues,
      backgroundColor: colorsForClass,
      borderColor: colorsForClass,
      borderWidth: 1,
    }],
  }), [classificationLabels, classificationValues, colorsForClass]);

  const pieData = useMemo(() => ({
    labels: farmLabels,
    datasets: [{
      data: farmValues,
      backgroundColor: colorsForFarm,
      hoverOffset: 8,
    }],
  }), [farmLabels, farmValues, colorsForFarm]);

  const commonOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: "bottom" }, tooltip: { enabled: true } },
  };
  const barOptions = { ...commonOptions, plugins: { ...commonOptions.plugins, legend: { position: "top" } }, scales: { y: { beginAtZero: true, ticks: { precision: 0 } } } };
  const pieOptions = { ...commonOptions, plugins: { ...commonOptions.plugins } };

  // modal placeholders
  const [modalDetails, setModalDetails] = useState({ visible: false, itemId: null, itemName: null });
  function handleConfirmDelete() { setModalDetails({ visible: false, itemId: null }); }

  return (
    <div>
      <span className="fs-3">Ventas</span>

      <div className="p-3">
        {/* filtros mínimos: periodo (rango), finca, lote */}
        <Grid className="mb-3" templateColumns={{ medium: "1fr 1fr 1fr" }} rowGap="15px" columnGap="15px">
          <div>
            <label className="form-label">Fecha desde</label>
            <input type="date" className="form-control" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>

          <div>
            <label className="form-label">Fecha hasta</label>
            <input type="date" className="form-control" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>

          <div>
            <label className="form-label">Finca</label>
            <select className="form-select" value={selectedFarmId} onChange={(e) => setSelectedFarmId(e.target.value)}>
              {(farms || []).map((f) => (<option key={f.id} value={f.id}>{f.name}</option>))}
            </select>
          </div>

          <div>
            <label className="form-label">Lote</label>
            <select className="form-select" value={selectedLotId} onChange={(e) => setSelectedLotId(e.target.value)}>
              {(lotsByFarm || []).map((l) => (<option key={l.id} value={l.id}>{l.name}</option>))}
            </select>
          </div>
        </Grid>

        {/* tarjetas mínimas */}
        <CRow className="mb-3">
          <CCol md={4} sm={12} className="mb-2">
            <CCard>
              <CCardHeader className="small text-muted">Total ventas (filtradas)</CCardHeader>
              <CCardBody>
                <div className="fs-4">{totalSalesAmount.toLocaleString("es-CO")}</div>
              </CCardBody>
            </CCard>
          </CCol>

          <CCol md={4} sm={12} className="mb-2">
            <CCard>
              <CCardHeader className="small text-muted">Registros</CCardHeader>
              <CCardBody>
                <div className="fs-4">{totalRecords}</div>
              </CCardBody>
            </CCard>
          </CCol>

          <CCol md={4} sm={12} className="mb-2">
            <CCard>
              <CCardHeader className="small text-muted">Promedio por venta</CCardHeader>
              <CCardBody>
                <div className="fs-4">{(totalRecords ? Math.round(totalSalesAmount / totalRecords) : 0).toLocaleString("es-CO")}</div>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>

        {/* gráficos */}
        <CRow>
          <CCol md={6} sm={12} className="mb-3">
            <CCard>
              <CCardHeader className="small text-muted">Ventas por Clasificación</CCardHeader>
              <CCardBody style={{ height: chartHeightPx, width: "100%" }}>
                {classificationLabels.length === 0 ? <div className="text-muted">No hay datos para el rango seleccionado.</div> : <Bar data={barData} options={barOptions} />}
              </CCardBody>
            </CCard>
          </CCol>

          <CCol md={6} sm={12} className="mb-3">
            <CCard>
              <CCardHeader className="small text-muted">Distribución por Finca</CCardHeader>
              <CCardBody style={{ height: chartHeightPx, width: "100%" }}>
                {farmLabels.length === 0 ? <div className="text-muted">No hay datos para el rango seleccionado.</div> : <Pie data={pieData} options={pieOptions} />}
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>

        {/* tabla con todos los registros filtrados */}
        <CRow>
          <CCol>
            <CCard>
              <CCardHeader>Registros de Ventas</CCardHeader>
              <CCardBody>
                <div className="table-responsive">
                  <CTable hover responsive>
                    <CTableHead>
                      <CTableRow>
                        <CTableHeaderCell>Fecha</CTableHeaderCell>
                        <CTableHeaderCell>Finca</CTableHeaderCell>
                        <CTableHeaderCell>Lote</CTableHeaderCell>
                        <CTableHeaderCell>Unidad de Cultivo</CTableHeaderCell>
                        <CTableHeaderCell>Persona que realiza</CTableHeaderCell>
                        <CTableHeaderCell>Clasificación</CTableHeaderCell>
                        <CTableHeaderCell>Total venta</CTableHeaderCell>
                        <CTableHeaderCell>Nombre del comprador</CTableHeaderCell>
                      </CTableRow>
                    </CTableHead>
                    <CTableBody>
                      {filteredSales.length === 0 ? (
                        <CTableRow>
                          <CTableDataCell colSpan={8} className="text-muted">No hay registros para los filtros seleccionados.</CTableDataCell>
                        </CTableRow>
                      ) : (
                        filteredSales.map((r) => (
                          <CTableRow key={r.id}>
                            <CTableDataCell>{r.date ?? "-"}</CTableDataCell>
                            <CTableDataCell>{r.farmName ?? r.farmId ?? "-"}</CTableDataCell>
                            <CTableDataCell>{r.lotName ?? r.lotId ?? "-"}</CTableDataCell>
                            <CTableDataCell>{r.cropUnit ?? "-"}</CTableDataCell>
                            <CTableDataCell>{r.performedByName ?? r.performedById ?? "-"}</CTableDataCell>
                            <CTableDataCell>{r.saleClassification ?? "-"}</CTableDataCell>
                            <CTableDataCell>{(Number(r.totalSale) || 0).toLocaleString("es-CO")}</CTableDataCell>
                            <CTableDataCell>{r.buyerName ?? "-"}</CTableDataCell>
                          </CTableRow>
                        ))
                      )}
                    </CTableBody>
                  </CTable>
                </div>

                {/* totalización al final */}
                <div className="mt-3 text-end">
                  <strong>Total (tabla): </strong> {totalSalesAmount.toLocaleString("es-CO")}
                </div>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </div>

      <ConfirmModal
        visible={modalDetails.visible}
        body={`¿Estás seguro de eliminar el registro ${modalDetails.itemName}?`}
        onCancel={() => setModalDetails({ visible: false, itemId: null })}
        onConfirm={handleConfirmDelete}
      />

      {/* Modal genérico (placeholder) */}
      <CModal size="xl" alignment="center" visible={false} onClose={() => {}} backdrop="static">
        <CModalHeader>
          <CModalTitle>Modal</CModalTitle>
        </CModalHeader>
        <CModalBody style={{ overflow: "hidden" }}>
          {/* placeholder content */}
        </CModalBody>
      </CModal>
    </div>
  );
}
